import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiPlusBold,
    PiTrashBold,
    PiPencilSimpleBold,
    PiArrowDownBold,
    PiArrowUpBold,
    PiFloppyDiskBold,
    PiXBold,
    PiToggleLeftBold,
    PiToggleRightFill,
    PiUploadSimpleBold,
    PiCaretDownBold,
    PiCaretRightBold,
    PiGitDiffBold,
} from 'react-icons/pi';
import { Badge, Button, PageHeader, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import type { CommissionModifier } from './types';
import { ROLE_LABELS } from './types';

type RuleVersionListItem = {
    id: number;
    version: string;
    display_version: string;
    label: string | null;
    notes: string | null;
    published_at: string | null;
    published_by: { id: number; name: string } | null;
    modifiers_count: number;
};

type RuleVersionDetail = RuleVersionListItem & {
    snapshot: {
        modifiers?: Array<{
            id?: number;
            name: string;
            type: string;
            condition_type: string;
            delta_rate?: number | null;
            per_pct_above_floor?: number | null;
            multiplier?: number;
            active?: boolean;
        }>;
        carencia?: { active?: boolean; deflator_per_month?: number };
    };
};

type DiffLine = { type: 'add' | 'remove' | 'context' | 'header'; content: string };

type RuleVersionDiff = {
    from: { id: number; version: string; display_version: string } | null;
    to: { id: number; version: string; display_version: string };
    summary: { added: number; removed: number; changed: number };
    sections: Array<{
        path: string;
        status: 'added' | 'removed' | 'changed';
        lines: DiffLine[];
    }>;
};

function GitHubDiffView({ diff }: { diff: RuleVersionDiff }) {
    const { t } = useI18n();
    const total = diff.summary.added + diff.summary.removed + diff.summary.changed;

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-semibold text-gray-700">
                    {diff.from
                        ? t('v2.commissioning.rule_versions.diff_title', {
                            from: diff.from.display_version,
                            to: diff.to.display_version,
                        })
                        : t('v2.commissioning.rule_versions.diff_initial', {
                            to: diff.to.display_version,
                        })}
                </span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">
                    +{diff.summary.added}
                </span>
                <span className="rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-800">
                    −{diff.summary.removed}
                </span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
                    ~{diff.summary.changed}
                </span>
            </div>

            {total === 0 ? (
                <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500 font-mono">
                    {t('v2.commissioning.rule_versions.diff_empty')}
                </p>
            ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-[#0d1117] text-[12px] leading-5 font-mono shadow-sm">
                    {diff.sections.map((section) => (
                        <div key={section.path} className="border-b border-[#21262d] last:border-b-0">
                            <div className="flex items-center gap-2 border-b border-[#21262d] bg-[#161b22] px-3 py-1.5 text-[#8b949e]">
                                <PiGitDiffBold className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">{section.path}</span>
                                <span
                                    className={
                                        section.status === 'added'
                                            ? 'ml-auto text-emerald-400'
                                            : section.status === 'removed'
                                              ? 'ml-auto text-red-400'
                                              : 'ml-auto text-amber-300'
                                    }
                                >
                                    {section.status === 'added'
                                        ? t('v2.commissioning.rule_versions.diff_status_added')
                                        : section.status === 'removed'
                                          ? t('v2.commissioning.rule_versions.diff_status_removed')
                                          : t('v2.commissioning.rule_versions.diff_status_changed')}
                                </span>
                            </div>
                            <div className="overflow-x-auto">
                                {section.lines.map((line, idx) => {
                                    const base =
                                        line.type === 'add'
                                            ? 'bg-[#12261e] text-[#aff5b4]'
                                            : line.type === 'remove'
                                              ? 'bg-[#2d1214] text-[#ffb1af]'
                                              : line.type === 'header'
                                                ? 'bg-[#161b22] text-[#8b949e]'
                                                : 'bg-[#0d1117] text-[#e6edf3]';
                                    const marker =
                                        line.type === 'add' ? '+' : line.type === 'remove' ? '−' : line.type === 'header' ? '@' : ' ';

                                    return (
                                        <div
                                            key={`${section.path}-${idx}`}
                                            className={`flex whitespace-pre px-0 ${base}`}
                                        >
                                            <span className="w-6 shrink-0 select-none border-r border-[#21262d]/40 text-center opacity-70">
                                                {marker}
                                            </span>
                                            <span className="flex-1 px-3 py-0.5">{line.content.replace(/^[-+]\s?/, '')}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

type CommissionSettingItem = { key: string; value: string; description: string | null };

function CarenciaSettingsCard() {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(false);

    const { data, isLoading } = useQuery<{ data: CommissionSettingItem[] }>({
        queryKey: ['commissioning', 'settings'],
        queryFn: () => api.get('/commissioning/settings').then((r) => r.data),
    });

    const carenciaItem = data?.data?.find((s) => s.key === 'carencia_deflator_per_month');
    const activeItem = data?.data?.find((s) => s.key === 'carencia_deflator_active');
    const isActive = activeItem ? activeItem.value !== '0' && activeItem.value !== 'false' : true;
    const currentPp = carenciaItem ? (parseFloat(carenciaItem.value) * 100).toFixed(2) : '0.20';
    const [ppInput, setPpInput] = useState('');

    const toggleMutation = useMutation({
        mutationFn: (active: boolean) =>
            api.put('/commissioning/settings', {
                settings: [{ key: 'carencia_deflator_active', value: active ? '1' : '0' }],
            }),
        onSuccess: (_, active) => {
            toast.success(active ? 'Deflator de carência ativado!' : 'Deflator de carência desativado.');
            qc.invalidateQueries({ queryKey: ['commissioning', 'settings'] });
        },
        onError: () => toast.error('Erro ao alterar status.'),
    });

    const saveMutation = useMutation({
        mutationFn: (ppValue: string) =>
            api.put('/commissioning/settings', {
                settings: [{
                    key: 'carencia_deflator_per_month',
                    value: String(parseFloat(ppValue) / 100),
                    description: 'Deflator por mês de carência (0,2 p.p. = 0.002 em decimal)',
                }],
            }),
        onSuccess: () => {
            toast.success('Deflator de carência salvo!');
            qc.invalidateQueries({ queryKey: ['commissioning', 'settings'] });
            setEditing(false);
        },
        onError: () => toast.error('Erro ao salvar.'),
    });

    function handleEdit() {
        setPpInput(currentPp);
        setEditing(true);
    }

    if (editing) {
        return (
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Deflator por mês de carência (p.p.)
                        </label>
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                autoFocus
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                                value={ppInput}
                                onChange={(e) => setPpInput(e.target.value)}
                            />
                            <span className="text-sm text-gray-500">p.p./mês</span>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setEditing(false)} className="flex items-center gap-1">
                        <PiXBold className="w-4 h-4" /> Cancelar
                    </Button>
                    <Button onClick={() => saveMutation.mutate(ppInput)} disabled={saveMutation.isPending} className="flex items-center gap-1">
                        <PiFloppyDiskBold className="w-4 h-4" />
                        {saveMutation.isPending ? 'Salvando…' : 'Salvar'}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={`rounded-xl border p-4 bg-white flex items-center gap-4 ${!isActive ? 'opacity-50' : 'border-gray-200'}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                        <PiArrowDownBold className="w-3 h-3" /> Defrator
                    </span>
                    <span className="font-medium text-gray-900">Carência — deflator por mês</span>
                    {!isActive && <span className="text-xs text-gray-400">(inativo)</span>}
                </div>
                <p className="text-xs text-gray-500">
                    Aplica a: <strong>Todos</strong>
                    {' · '}Condição: <strong>Meses de carência do cliente</strong>
                    {' · '}Delta:{' '}
                    {isLoading
                        ? <span className="text-gray-400">…</span>
                        : <strong className="text-red-600">−{currentPp} p.p./mês</strong>
                    }
                </p>
            </div>
            <div className="flex items-center gap-1">
                <button
                    title={isActive ? 'Desativar' : 'Ativar'}
                    disabled={toggleMutation.isPending}
                    onClick={() => toggleMutation.mutate(!isActive)}
                    className={`p-2 rounded-lg transition-colors ${isActive ? 'text-emerald-600 hover:bg-emerald-50' : 'text-gray-400 hover:bg-gray-100'}`}
                >
                    {isActive
                        ? <PiToggleRightFill className="w-5 h-5" />
                        : <PiToggleLeftBold className="w-5 h-5" />}
                </button>
                <button onClick={handleEdit} className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">
                    <PiPencilSimpleBold className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

const BLANK: Omit<CommissionModifier, 'id' | 'type_label' | 'condition_label'> = {
    name: '',
    type: 'defrater',
    applies_to: 'all',
    condition_type: 'manual',
    condition_params: null,
    multiplier: 1,
    active: true,
    sort_order: 0,
    notes: null,
};

function ModifierBadge({ type }: { type: string }) {
    return type === 'accelerator' ? (
        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
            <PiArrowUpBold className="w-3 h-3" /> Acelerador
        </span>
    ) : (
        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
            <PiArrowDownBold className="w-3 h-3" /> Defrator
        </span>
    );
}

function ModifierForm({
    initial,
    onSave,
    onCancel,
    loading,
}: {
    initial: Partial<CommissionModifier>;
    onSave: (data: Partial<CommissionModifier>) => void;
    onCancel: () => void;
    loading: boolean;
}) {
    const [form, setForm] = useState({ ...BLANK, ...initial });
    const set = (field: string, value: unknown) => setForm((p) => ({ ...p, [field]: value }));

    return (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nome</label>
                    <input
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                        placeholder="Ex: Meta abaixo de 80%"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
                    <select
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        value={form.type}
                        onChange={(e) => set('type', e.target.value)}
                    >
                        <option value="defrater">Defrator (reduz comissão)</option>
                        <option value="accelerator">Acelerador (aumenta comissão)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Aplica a</label>
                    <select
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        value={form.applies_to}
                        onChange={(e) => set('applies_to', e.target.value)}
                    >
                        <option value="all">Todos os níveis</option>
                        {Object.entries(ROLE_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Condição</label>
                    <select
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        value={form.condition_type}
                        onChange={(e) => set('condition_type', e.target.value)}
                    >
                        <option value="meta_range">Faixa de Meta (%)</option>
                        <option value="product_type">Tipo de Produto</option>
                        <option value="payment_term">Prazo / Inadimplência</option>
                        <option value="entry_range">Faixa de Entrada (%)</option>
                        <option value="entry_accelerator">Entrada Elevada (acelerador)</option>
                        <option value="deal_amount_min">Volume do Negócio (acelerador)</option>
                        <option value="overfloor_per_pct">Desconto acima de 30% — 0,1 p.p./1%</option>
                        <option value="manual">Aplicação Manual</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                        Multiplicador (ex: 0.8 = −20%, 1.2 = +20%)
                        <span className="text-gray-400 font-normal"> — não usado em faixas de entrada</span>
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="10"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        value={form.multiplier}
                        onChange={(e) => set('multiplier', parseFloat(e.target.value))}
                    />
                </div>
                {(form.condition_type === 'entry_range') && (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Delta em p.p. (negativo = deflator, ex: -0.002 = −0,2 p.p.)
                        </label>
                        <input
                            type="number"
                            step="0.0001"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                            value={(form as Record<string,unknown>).delta_rate as number ?? 0}
                            onChange={(e) => set('delta_rate', parseFloat(e.target.value))}
                        />
                    </div>
                )}
                {form.condition_type === 'overfloor_per_pct' && (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Redução por 1% acima do teto (ex: 0.001 = 0,1 p.p.)
                        </label>
                        <input
                            type="number"
                            step="0.0001"
                            min="0"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                            value={(form as Record<string,unknown>).per_pct_above_floor as number ?? 0}
                            onChange={(e) => set('per_pct_above_floor', parseFloat(e.target.value))}
                        />
                    </div>
                )}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Ordem</label>
                    <input
                        type="number"
                        min="0"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        value={form.sort_order}
                        onChange={(e) => set('sort_order', parseInt(e.target.value))}
                    />
                </div>
            </div>
            {(form.condition_type === 'meta_range' || form.condition_type === 'entry_range') && (
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            {form.condition_type === 'entry_range' ? 'Entrada Mínima (%)' : 'Meta Mínima (%)'}
                        </label>
                        <input
                            type="number"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            value={(form.condition_params as Record<string,number>)?.min ?? 0}
                            onChange={(e) => set('condition_params', { ...((form.condition_params as Record<string,number>) ?? {}), min: parseFloat(e.target.value) })}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            {form.condition_type === 'entry_range' ? 'Entrada Máxima (%)' : 'Meta Máxima (%)'}
                        </label>
                        <input
                            type="number"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            value={(form.condition_params as Record<string,number>)?.max ?? 100}
                            onChange={(e) => set('condition_params', { ...((form.condition_params as Record<string,number>) ?? {}), max: parseFloat(e.target.value) })}
                        />
                    </div>
                </div>
            )}
            <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Observações</label>
                <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={form.notes ?? ''}
                    onChange={(e) => set('notes', e.target.value || null)}
                />
            </div>
            <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} className="rounded" />
                    Ativo
                </label>
            </div>
            <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={onCancel}>Cancelar</Button>
                <Button onClick={() => onSave(form)} disabled={loading}>
                    {loading ? 'Salvando…' : 'Salvar'}
                </Button>
            </div>
        </div>
    );
}

function RuleVersionsPanel() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const [showPublish, setShowPublish] = useState(false);
    const [bump, setBump] = useState<'patch' | 'minor' | 'major'>('patch');
    const [notes, setNotes] = useState('');
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [viewMode, setViewMode] = useState<'diff' | 'snapshot'>('diff');
    const [diffBaseId, setDiffBaseId] = useState<number | 'prev'>('prev');

    const { data, isLoading } = useQuery<{ data: RuleVersionListItem[]; current: RuleVersionListItem | null }>({
        queryKey: ['commissioning', 'rule-versions'],
        queryFn: () => api.get('/commissioning/rule-versions').then((r) => r.data),
    });

    const detailQuery = useQuery<{ data: RuleVersionDetail }>({
        queryKey: ['commissioning', 'rule-versions', expandedId],
        queryFn: () => api.get(`/commissioning/rule-versions/${expandedId}`).then((r) => r.data),
        enabled: expandedId != null && viewMode === 'snapshot',
    });

    const diffQuery = useQuery<{ data: RuleVersionDiff }>({
        queryKey: ['commissioning', 'rule-versions', expandedId, 'diff', diffBaseId],
        queryFn: () => {
            const params = diffBaseId === 'prev' ? '' : `?base=${diffBaseId}`;
            return api.get(`/commissioning/rule-versions/${expandedId}/diff${params}`).then((r) => r.data);
        },
        enabled: expandedId != null && viewMode === 'diff',
    });

    const publishMutation = useMutation({
        mutationFn: () =>
            api.post('/commissioning/rule-versions', { bump, notes: notes.trim() || null }),
        onSuccess: (res) => {
            const version = res.data?.data?.display_version ?? res.data?.data?.version;
            toast.success(t('v2.commissioning.rule_versions.publish_success', { version: version ?? '' }));
            qc.invalidateQueries({ queryKey: ['commissioning', 'rule-versions'] });
            setShowPublish(false);
            setNotes('');
            setBump('patch');
            setHistoryOpen(true);
            setViewMode('diff');
        },
        onError: () => toast.error(t('v2.commissioning.rule_versions.publish_error')),
    });

    const current = data?.current ?? null;
    const versions = data?.data ?? [];
    const detail = detailQuery.data?.data;
    const diff = diffQuery.data?.data;
    const olderVersions = versions.filter((v) => expandedId != null && v.id < expandedId);

    return (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-2">
                    <Badge className="bg-indigo-100 text-indigo-800">
                        {current
                            ? t('v2.commissioning.rule_versions.current_badge', { version: current.display_version })
                            : t('v2.commissioning.rule_versions.no_version')}
                    </Badge>
                    {current?.notes && (
                        <span className="text-xs text-gray-500 truncate max-w-xs">{current.notes}</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setHistoryOpen((v) => !v)}
                        className="flex items-center gap-1 text-sm"
                    >
                        {historyOpen ? <PiCaretDownBold className="w-4 h-4" /> : <PiCaretRightBold className="w-4 h-4" />}
                        {t('v2.commissioning.rule_versions.history')}
                    </Button>
                    <Button
                        onClick={() => setShowPublish((v) => !v)}
                        className="flex items-center gap-1.5"
                    >
                        <PiUploadSimpleBold className="w-4 h-4" />
                        {t('v2.commissioning.rule_versions.publish')}
                    </Button>
                </div>
            </div>

            {showPublish && (
                <div className="rounded-lg border border-indigo-200 bg-white p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Bump</label>
                            <select
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                value={bump}
                                onChange={(e) => setBump(e.target.value as 'patch' | 'minor' | 'major')}
                            >
                                <option value="patch">{t('v2.commissioning.rule_versions.bump_patch')}</option>
                                <option value="minor">{t('v2.commissioning.rule_versions.bump_minor')}</option>
                                <option value="major">{t('v2.commissioning.rule_versions.bump_major')}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t('v2.commissioning.rule_versions.notes_label')}
                            </label>
                            <input
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={t('v2.commissioning.rule_versions.notes_placeholder')}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowPublish(false)}>
                            <PiXBold className="w-4 h-4" />
                        </Button>
                        <Button
                            onClick={() => publishMutation.mutate()}
                            disabled={publishMutation.isPending}
                            className="flex items-center gap-1"
                        >
                            <PiUploadSimpleBold className="w-4 h-4" />
                            {publishMutation.isPending
                                ? t('v2.commissioning.rule_versions.publishing')
                                : t('v2.commissioning.rule_versions.publish')}
                        </Button>
                    </div>
                </div>
            )}

            {historyOpen && (
                <div className="space-y-2">
                    {isLoading && <LoadingState variant="inline" />}
                    {!isLoading && versions.length === 0 && (
                        <p className="text-sm text-gray-500">{t('v2.commissioning.rule_versions.history_empty')}</p>
                    )}
                    {versions.map((v) => {
                        const open = expandedId === v.id;
                        return (
                            <div key={v.id} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                <button
                                    type="button"
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
                                    onClick={() => {
                                        if (open) {
                                            setExpandedId(null);
                                        } else {
                                            setExpandedId(v.id);
                                            setDiffBaseId('prev');
                                            setViewMode('diff');
                                        }
                                    }}
                                >
                                    {open ? <PiCaretDownBold className="w-4 h-4 text-gray-400" /> : <PiCaretRightBold className="w-4 h-4 text-gray-400" />}
                                    <span className="font-semibold text-indigo-700">{v.display_version}</span>
                                    {v.label && <span className="text-xs text-gray-500">{v.label}</span>}
                                    <span className="text-xs text-gray-400 ml-auto">
                                        {v.published_at
                                            ? t('v2.commissioning.rule_versions.published_at', {
                                                date: new Date(v.published_at).toLocaleString('pt-BR'),
                                            })
                                            : ''}
                                        {v.published_by
                                            ? ` ${t('v2.commissioning.rule_versions.published_by', { name: v.published_by.name })}`
                                            : ''}
                                        {' · '}
                                        {t('v2.commissioning.rule_versions.modifiers_count', { count: v.modifiers_count })}
                                    </span>
                                </button>
                                {open && (
                                    <div className="border-t border-gray-100 px-3 py-3 space-y-3">
                                        {v.notes && <p className="text-xs text-gray-500">{v.notes}</p>}

                                        <div className="flex flex-wrap items-center gap-2">
                                            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-xs">
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('diff')}
                                                    className={`flex items-center gap-1 rounded-md px-2.5 py-1 font-medium ${
                                                        viewMode === 'diff'
                                                            ? 'bg-white text-indigo-700 shadow-sm'
                                                            : 'text-gray-500 hover:text-gray-700'
                                                    }`}
                                                >
                                                    <PiGitDiffBold className="h-3.5 w-3.5" />
                                                    {t('v2.commissioning.rule_versions.diff_tab')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('snapshot')}
                                                    className={`rounded-md px-2.5 py-1 font-medium ${
                                                        viewMode === 'snapshot'
                                                            ? 'bg-white text-indigo-700 shadow-sm'
                                                            : 'text-gray-500 hover:text-gray-700'
                                                    }`}
                                                >
                                                    {t('v2.commissioning.rule_versions.snapshot_tab')}
                                                </button>
                                            </div>

                                            {viewMode === 'diff' && olderVersions.length > 0 && (
                                                <label className="flex items-center gap-1.5 text-xs text-gray-600">
                                                    <span>{t('v2.commissioning.rule_versions.diff_compare_with')}</span>
                                                    <select
                                                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs"
                                                        value={diffBaseId === 'prev' ? 'prev' : String(diffBaseId)}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setDiffBaseId(val === 'prev' ? 'prev' : Number(val));
                                                        }}
                                                    >
                                                        <option value="prev">
                                                            {t('v2.commissioning.rule_versions.diff_previous')}
                                                        </option>
                                                        {olderVersions.map((base) => (
                                                            <option key={base.id} value={base.id}>
                                                                {base.display_version}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            )}
                                        </div>

                                        {viewMode === 'diff' && (
                                            <>
                                                {diffQuery.isPending && <LoadingState variant="inline" />}
                                                {diffQuery.isError && (
                                                    <p className="text-xs text-red-600">
                                                        {t('v2.commissioning.rule_versions.diff_error')}
                                                    </p>
                                                )}
                                                {diff && <GitHubDiffView diff={diff} />}
                                            </>
                                        )}

                                        {viewMode === 'snapshot' && (
                                            <>
                                                {detailQuery.isPending && <LoadingState variant="inline" />}
                                                {detail && detail.id === v.id && (
                                                    <>
                                                        <p className="text-xs font-medium text-gray-700">
                                                            {t('v2.commissioning.rule_versions.snapshot_title', { version: detail.display_version })}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {t('v2.commissioning.rule_versions.carencia_label')}:{' '}
                                                            {detail.snapshot?.carencia?.active === false
                                                                ? t('v2.commissioning.rule_versions.active_no')
                                                                : `−${(((detail.snapshot?.carencia?.deflator_per_month ?? 0.002) * 100)).toFixed(2)} p.p./mês`}
                                                        </p>
                                                        <div className="overflow-x-auto">
                                                            <table className="min-w-full text-xs">
                                                                <thead>
                                                                    <tr className="text-left text-gray-500 border-b">
                                                                        <th className="py-1.5 pr-3">{t('v2.commissioning.rule_versions.col_name')}</th>
                                                                        <th className="py-1.5 pr-3">{t('v2.commissioning.rule_versions.col_type')}</th>
                                                                        <th className="py-1.5 pr-3">{t('v2.commissioning.rule_versions.col_condition')}</th>
                                                                        <th className="py-1.5 pr-3">{t('v2.commissioning.rule_versions.col_delta')}</th>
                                                                        <th className="py-1.5">{t('v2.commissioning.rule_versions.col_active')}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(detail.snapshot?.modifiers ?? []).map((m, idx) => (
                                                                        <tr key={m.id ?? idx} className="border-b border-gray-50">
                                                                            <td className="py-1.5 pr-3 font-medium text-gray-800">{m.name}</td>
                                                                            <td className="py-1.5 pr-3">
                                                                                {m.type === 'accelerator'
                                                                                    ? t('v2.commissioning.rule_versions.type_accelerator')
                                                                                    : t('v2.commissioning.rule_versions.type_defrater')}
                                                                            </td>
                                                                            <td className="py-1.5 pr-3 text-gray-600">{m.condition_type}</td>
                                                                            <td className="py-1.5 pr-3 tabular-nums">
                                                                                {m.delta_rate != null
                                                                                    ? `${(m.delta_rate * 100).toFixed(2)} p.p.`
                                                                                    : m.per_pct_above_floor != null
                                                                                      ? `${(m.per_pct_above_floor * 100).toFixed(2)} p.p./1%`
                                                                                      : `×${(m.multiplier ?? 1).toFixed(2)}`}
                                                                            </td>
                                                                            <td className="py-1.5">
                                                                                {m.active === false
                                                                                    ? t('v2.commissioning.rule_versions.active_no')
                                                                                    : t('v2.commissioning.rule_versions.active_yes')}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function CommissionModifiersPage() {
    const qc = useQueryClient();
    const [editing, setEditing] = useState<CommissionModifier | null>(null);
    const [adding, setAdding] = useState(false);

    const { data, isLoading } = useQuery<{ data: CommissionModifier[] }>({
        queryKey: ['commissioning', 'modifiers'],
        queryFn: () => api.get('/commissioning/modifiers').then((r) => r.data),
    });

    const modifiers = data?.data ?? [];

    const createMutation = useMutation({
        mutationFn: (d: Partial<CommissionModifier>) => api.post('/commissioning/modifiers', d),
        onSuccess: () => { toast.success('Modificador criado!'); qc.invalidateQueries({ queryKey: ['commissioning', 'modifiers'] }); setAdding(false); },
        onError: () => toast.error('Erro ao criar.'),
    });

    const updateMutation = useMutation({
        mutationFn: (d: Partial<CommissionModifier>) => api.put(`/commissioning/modifiers/${d.id}`, d),
        onSuccess: () => { toast.success('Modificador atualizado!'); qc.invalidateQueries({ queryKey: ['commissioning', 'modifiers'] }); setEditing(null); },
        onError: () => toast.error('Erro ao atualizar.'),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => api.delete(`/commissioning/modifiers/${id}`),
        onSuccess: () => { toast.success('Removido!'); qc.invalidateQueries({ queryKey: ['commissioning', 'modifiers'] }); },
    });

    const toggleMutation = useMutation({
        mutationFn: (m: CommissionModifier) => api.put(`/commissioning/modifiers/${m.id}`, { ...m, active: !m.active }),
        onSuccess: (_, m) => {
            toast.success(m.active ? 'Modificador desativado.' : 'Modificador ativado!');
            qc.invalidateQueries({ queryKey: ['commissioning', 'modifiers'] });
        },
        onError: () => toast.error('Erro ao alterar status.'),
    });

    return (
        <div className="space-y-6">
            <PageHeader
                title="Defratores e Aceleradores"
                subtitle="Regras dinâmicas que ajustam o valor final da comissão"
            >
                <Button onClick={() => setAdding(true)} className="flex items-center gap-2">
                    <PiPlusBold className="w-4 h-4" /> Novo Modificador
                </Button>
            </PageHeader>

            <RuleVersionsPanel />

            <CarenciaSettingsCard />

            {adding && (
                <ModifierForm
                    initial={{}}
                    onSave={(d) => createMutation.mutate(d)}
                    onCancel={() => setAdding(false)}
                    isLoading={createMutation.isPending}
                />
            )}

            {isLoading && <LoadingState variant="table" />}

            <div className="space-y-3">
                {modifiers.map((m) => (
                    <div key={m.id}>
                        {editing?.id === m.id ? (
                            <ModifierForm
                                initial={m}
                                onSave={(d) => updateMutation.mutate({ ...d, id: m.id })}
                                onCancel={() => setEditing(null)}
                                isLoading={updateMutation.isPending}
                            />
                        ) : (
                            <div className={`rounded-xl border p-4 bg-white flex items-center gap-4 ${!m.active ? 'opacity-50' : 'border-gray-200'}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <ModifierBadge type={m.type} />
                                        <span className="font-medium text-gray-900">{m.name}</span>
                                        {!m.active && <span className="text-xs text-gray-400">(inativo)</span>}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Aplica a: <strong>{m.applies_to === 'all' ? 'Todos' : (ROLE_LABELS[m.applies_to as keyof typeof ROLE_LABELS] ?? m.applies_to)}</strong>
                                        {' · '}Condição: <strong>{m.condition_label}</strong>
                                        {m.condition_type === 'entry_range' && (m as Record<string,unknown>).delta_rate != null ? (
                                            <> · Delta: <strong className="text-red-600">{(((m as Record<string,unknown>).delta_rate as number) * 100).toFixed(2)} p.p.</strong></>
                                        ) : m.condition_type === 'overfloor_per_pct' && (m as Record<string,unknown>).per_pct_above_floor != null ? (
                                            <> · Redução: <strong className="text-red-600">{(((m as Record<string,unknown>).per_pct_above_floor as number) * 100).toFixed(2)} p.p./1%</strong></>
                                        ) : (
                                            <> · Multiplicador: <strong>×{m.multiplier.toFixed(4)}</strong></>
                                        )}
                                    </p>
                                    {m.notes && <p className="text-xs text-gray-400 mt-0.5">{m.notes}</p>}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        title={m.active ? 'Desativar' : 'Ativar'}
                                        disabled={toggleMutation.isPending}
                                        onClick={() => toggleMutation.mutate(m)}
                                        className={`p-2 rounded-lg transition-colors ${
                                            m.active
                                                ? 'text-emerald-600 hover:bg-emerald-50'
                                                : 'text-gray-400 hover:bg-gray-100'
                                        }`}
                                    >
                                        {m.active
                                            ? <PiToggleRightFill className="w-5 h-5" />
                                            : <PiToggleLeftBold className="w-5 h-5" />}
                                    </button>
                                    <button onClick={() => setEditing(m)} className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">
                                        <PiPencilSimpleBold className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => { if (confirm('Remover modificador?')) deleteMutation.mutate(m.id); }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                        <PiTrashBold className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {modifiers.length === 0 && !isLoading && !adding && (
                    <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-500 text-sm">
                        Nenhum modificador cadastrado. Clique em "Novo Modificador" para começar.
                    </div>
                )}
            </div>
        </div>
    );
}
